import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import { MovementTypeBadge } from "./MovementTypeBadge";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

function displayValue(value) {
  return value == null || value === "" ? "—" : String(value);
}

function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint font-semibold shrink-0">{label}</span>
      <div className="text-right min-w-0">{children}</div>
    </div>
  );
}

function normalizeContentItem(item) {
  if (!item || typeof item !== "object") return null;
  const productId = item.product_id ?? item.productId ?? null;
  const productName = item.product_name ?? item.productName ?? "";
  const partNumber = item.part_number ?? item.partNumber ?? "";
  const uomId = item.uom_id ?? item.uomId ?? null;
  const quantity =
    item.allocated_quantity ??
    item.allocatedQuantity ??
    item.quantity ??
    null;
  const batchNumber = item.batch_number ?? item.batchNumber ?? "";
  const price = item.price ?? null;

  if (
    productId == null &&
    !productName &&
    !partNumber &&
    quantity == null &&
    !batchNumber &&
    price == null
  ) {
    return null;
  }

  return {
    productId,
    productName,
    partNumber,
    uomId,
    quantity,
    batchNumber,
    price,
  };
}

function coerceContentItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Fallback when contentItems is empty but deliveryContent HTML is present. */
function parseDeliveryContentItems(html) {
  const raw = String(html || "").trim();
  if (!raw) return [];
  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const rows = Array.from(doc.querySelectorAll("tbody tr"));
    return rows
      .map((tr) => {
        const cells = Array.from(tr.querySelectorAll("td")).map((td) =>
          String(td.textContent || "").trim()
        );
        if (cells.length === 0) return null;
        // Expected: Product Id | Product Name | Part Number | UOM Id | Quantity | Batch Number | Price
        return normalizeContentItem({
          product_id: cells[0] || null,
          product_name: cells[1] || "",
          part_number: cells[2] || "",
          uom_id: cells[3] || null,
          allocated_quantity: cells[4] || null,
          batch_number: cells[5] || "",
          price: cells[6] || null,
        });
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

function resolveContentItems(challan) {
  const fromApi = coerceContentItems(challan?.contentItems)
    .map(normalizeContentItem)
    .filter(Boolean);
  if (fromApi.length > 0) return fromApi;
  return parseDeliveryContentItems(challan?.deliveryContent);
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   challan?: {
 *     deliveryChallanId?: number|string,
 *     challanDate?: string,
 *     movementType?: string,
 *     stockJournalId?: number|null,
 *     challanTo?: number|null,
 *     customerAllocationId?: number|null,
 *     deliveryContent?: string,
 *     contentItems?: Array<Record<string, unknown>> | string,
 *   } | null,
 *   submitting?: boolean,
 *   submitError?: string,
 *   onSubmit?: (invoiceNumber: string) => void,
 * }} props
 */
export function AddInvoiceNumberModal({
  open,
  onOpenChange,
  challan = null,
  submitting = false,
  submitError = "",
  onSubmit,
}) {
  const { t } = useTranslation();
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setInvoiceNumber("");
    setError("");
  }, [open, challan?.deliveryChallanId]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const match = String(isoStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dateObj = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(isoStr);
    if (!Number.isFinite(dateObj.getTime())) return "—";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const contentItems = useMemo(
    () => resolveContentItems(challan),
    [challan]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = invoiceNumber.trim();
    if (!value) {
      setError(
        t("validation.fieldRequired", {
          field: t("inventory.invoiceNumber"),
        })
      );
      return;
    }
    onSubmit?.(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[720px] max-h-[90vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <FileText className="h-4.5 w-4.5 text-primary" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("inventory.addInvoiceNumber")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close
              disabled={submitting}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <p className="text-xs per-file-hint font-semibold leading-relaxed shrink-0">
            {t("inventory.addInvoiceNumberHint")}
          </p>

          <div className="min-h-0 flex-1 overflow-y-auto space-y-4 pr-0.5">
            <div className="rounded-xl border border-(--color-text-border)/50 bg-workspace/20 px-3.5 py-3 space-y-2">
              <DetailRow label={t("inventory.challanId")}>
                <span className="font-mono font-bold text-foreground">
                  {displayValue(challan?.deliveryChallanId)}
                </span>
              </DetailRow>
              <DetailRow label={t("dateTime.date")}>
                <span className="font-semibold text-foreground">
                  {formatDate(challan?.challanDate)}
                </span>
              </DetailRow>
              <DetailRow label={t("inventory.movementType")}>
                <MovementTypeBadge type={challan?.movementType} />
              </DetailRow>
              <DetailRow label={t("inventory.challanTo")}>
                <span className="font-mono font-semibold text-foreground">
                  {displayValue(challan?.challanTo)}
                </span>
              </DetailRow>
              <DetailRow label={t("inventory.stockJournal")}>
                <span className="font-mono font-semibold text-foreground">
                  {displayValue(challan?.stockJournalId)}
                </span>
              </DetailRow>
              <DetailRow label={t("inventory.customerAllocation")}>
                <span className="font-mono font-semibold text-foreground">
                  {displayValue(challan?.customerAllocationId)}
                </span>
              </DetailRow>
            </div>

            <div className="rounded-xl border border-(--color-text-border)/50 overflow-hidden">
              <div className="px-3.5 py-2 bg-gray-50/80 border-b border-(--color-text-border)/50">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
                  {t("inventory.products")}
                </span>
              </div>
              {contentItems.length === 0 ? (
                <div className="px-3.5 py-4 text-xs font-semibold per-file-hint text-center">
                  {t("inventory.noProductsOnChallan")}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[640px]">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider per-file-hint bg-white">
                        <th className="px-3 py-2 font-bold whitespace-nowrap">
                          {t("inventory.productIdLabel")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">
                          {t("inventory.product")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">
                          {t("inventory.partNo")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">
                          {t("productMetrics.uom")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap text-right">
                          {t("inventory.quantity")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">
                          {t("inventory.batchNumber")}
                        </th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap text-right">
                          {t("inventory.price")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-(--color-text-border)/35">
                      {contentItems.map((item, idx) => (
                        <tr
                          key={`${item.productId || idx}-${item.batchNumber || idx}`}
                        >
                          <td className="px-3 py-2 font-mono per-file-hint whitespace-nowrap">
                            {displayValue(item.productId)}
                          </td>
                          <td className="px-3 py-2 font-semibold text-foreground">
                            {displayValue(item.productName)}
                          </td>
                          <td className="px-3 py-2 font-mono per-file-hint whitespace-nowrap">
                            {displayValue(item.partNumber)}
                          </td>
                          <td className="px-3 py-2 font-mono per-file-hint whitespace-nowrap">
                            {displayValue(item.uomId)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                            {displayValue(item.quantity)}
                          </td>
                          <td className="px-3 py-2 font-mono per-file-hint whitespace-nowrap">
                            {displayValue(item.batchNumber)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                            {displayValue(item.price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 shrink-0"
          >
            <div className="flex flex-col gap-2">
              <label
                htmlFor="delivery-challan-invoice-number"
                className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400"
              >
                {t("inventory.invoiceNumber")}{" "}
                <span className="text-rose-500">*</span>
              </label>
              <input
                id="delivery-challan-invoice-number"
                type="text"
                value={invoiceNumber}
                onChange={(e) => {
                  setInvoiceNumber(e.target.value);
                  if (error) setError("");
                }}
                disabled={submitting}
                autoFocus
                className={cn(
                  "w-full h-10 px-3 rounded-xl border bg-white text-sm font-semibold",
                  "focus:outline-hidden focus:ring-2 focus:ring-accent/10",
                  error || submitError
                    ? "border-rose-500 focus:border-rose-500"
                    : "border-(--color-text-border) focus:border-accent"
                )}
              />
            </div>

            {error || submitError ? (
              <p className="text-[11px] text-rose-500 font-bold">
                ⚠️ {error || submitError}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
                className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="h-10 px-5 rounded-xl bg-primary hover:bg-accent text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t("inventory.saving") : t("formActions.save")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
